import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mfaService } from '@shared/services/mfa';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Button, Input } from '@shared/components/ui';
import { ShieldCheck, ArrowLeft, Copy, CheckCircle2, X, Smartphone } from 'lucide-react';

export default function MFASetup() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { showSuccess } = useDialog();
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [totpCode, setTotpCode] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const initializeMFA = async () => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await mfaService.setupMFA();
      setQrCodeUrl(res.qr_code_url);
      setBackupCodes(res.backup_codes);
    } catch {
      setError(t('errors.setupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || totpCode.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await mfaService.enableMFA({ totp_code: totpCode });
      setStep(3);
    } catch {
      setError(t('errors.invalidMfa'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-300">
        <div className="flex items-start gap-3 sm:items-center sm:gap-4">
          <Button
            variant="ghost"
            size="md"
            onClick={() => navigate('/profile')}
            aria-label={t('mfa.returnToProfile')}
            disabled={loading}
            className="p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
          >
            <ArrowLeft
              size={20}
              aria-hidden="true"
              className="text-text-subtle-light dark:text-text-subtle-dark"
            />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
              {t('mfa.setupTitle')}
            </h1>
            <p className="text-sm sm:text-base text-text-muted-light dark:text-text-muted-dark mt-1">
              {t('mfa.setupSubtitle')}
            </p>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 p-4 rounded-xl flex items-center gap-3 text-state-danger-light dark:text-state-danger-dark font-bold"
          >
            <X size={20} /> {error}
          </div>
        )}

        <Card className="p-5 sm:p-8">
          {/* Step Indicators */}
          <div
            role="group"
            aria-label={t('mfa.setupProgress')}
            className="flex items-center justify-between mb-8 sm:mb-12"
          >
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all ${
                    step === s
                      ? 'bg-brand-600 text-white shadow-brand-200 ring-4 ring-accent-soft-light dark:ring-accent-soft-dark'
                      : step > s
                        ? 'bg-state-ok-light dark:bg-state-ok-dark text-on-accent-light dark:text-on-accent-dark'
                        : 'bg-surface-2-light dark:bg-surface-2-dark text-text-subtle-light dark:text-text-subtle-dark'
                  }`}
                >
                  {step > s ? <CheckCircle2 size={20} /> : s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-12 h-1 rounded-full ${step > s ? 'bg-state-ok-light/40 dark:bg-state-ok-dark/40' : 'bg-surface-2-light dark:bg-surface-2-dark'}`}
                  />
                )}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-8 text-center animate-in slide-in-from-bottom duration-500">
              {qrCodeUrl ? (
                <>
                  <div className="p-6 bg-white dark:bg-white inline-block rounded-3xl border-4 border-surface-border-light dark:border-surface-border-dark shadow-soft">
                    <img src={qrCodeUrl} alt={t('mfa.qrCodeAlt')} className="w-48 h-48" />
                  </div>
                  <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                    {t('mfa.scanCodePrompt')}
                  </p>
                </>
              ) : (
                <div
                  className="mx-auto flex h-48 w-48 items-center justify-center rounded-3xl bg-surface-2-light dark:bg-surface-2-dark text-sm text-text-muted-light dark:text-text-muted-dark"
                  role="status"
                >
                  {loading ? t('mfa.loadingSetup') : t('mfa.setupPrompt')}
                </div>
              )}

              <div className="pt-6 border-t border-surface-border-light dark:border-surface-border-dark">
                {qrCodeUrl ? (
                  <Button size="lg" className="w-full" onClick={() => setStep(2)}>
                    {t('mfa.scannedCode')}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className="w-full"
                    onClick={initializeMFA}
                    isLoading={loading}
                    disabled={loading}
                  >
                    {error ? t('mfa.retrySetup') : t('mfa.startSetup')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8 text-center animate-in slide-in-from-bottom duration-500">
              <div className="space-y-2">
                <h3 className="text-xl sm:text-2xl font-semibold text-text-primary-light dark:text-text-primary-dark tracking-tight">
                  {t('mfa.codeLabel')}
                </h3>
                <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
                  {t('mfa.codePlaceholder')}
                </p>
              </div>

              <form onSubmit={handleEnableMFA} className="space-y-8">
                <label htmlFor="mfa-totp-code" className="sr-only">
                  {t('mfa.codeLabel')}
                </label>
                <Input
                  id="mfa-totp-code"
                  disabled={loading}
                  pattern="[0-9]{6}"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  placeholder={t('mfa.mfaPlaceholder')}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-label={t('mfa.codeLabel')}
                  className="text-center text-3xl font-bold tracking-[0.4em] h-16"
                  required
                />
                <div className="flex gap-3">
                  <Button
                    type="button"
                    disabled={loading}
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setStep(1)}
                  >
                    {t('mfa.back')}
                  </Button>
                  <Button className="flex-1" type="submit" isLoading={loading}>
                    {t('mfa.verifyButton')}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8 animate-in slide-in-from-bottom duration-500">
              <div className="space-y-2 text-center">
                <div className="w-16 h-16 bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck size={32} />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-text-primary-light dark:text-text-primary-dark tracking-tight">
                  {t('mfa.backupCodesTitle')}
                </h3>
                <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
                  {t('mfa.backupCodesSubtitle')}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {backupCodes.map((code, i) => (
                  <div
                    key={i}
                    className="p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl border border-surface-border-light dark:border-surface-border-dark flex justify-between items-center group"
                  >
                    <code className="min-w-0 break-all font-mono font-bold text-text-primary-light dark:text-text-primary-dark">
                      {code}
                    </code>
                    <button
                      type="button"
                      aria-label={t('mfa.copyCode', { code })}
                      onClick={async () => {
                        setError('');
                        try {
                          await navigator.clipboard.writeText(code);
                          await showSuccess(t('mfa.codesCopied'), t('mfa.codeCopiedMessage'));
                        } catch {
                          setError(t('errors.copyFailed'));
                        }
                      }}
                      className="min-h-11 min-w-11 shrink-0 text-text-subtle-light dark:text-text-subtle-dark hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t border-surface-border-light dark:border-surface-border-dark">
                <Button className="w-full" size="lg" onClick={() => navigate('/profile')}>
                  {t('mfa.returnToProfile')}
                </Button>
              </div>
            </div>
          )}
        </Card>

        {step < 3 && (
          <div className="flex items-center gap-3 p-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark rounded-2xl border border-state-warn-light/20 dark:border-state-warn-dark/20">
            <Smartphone
              className="text-state-warn-light dark:text-state-warn-dark shrink-0"
              size={24}
            />
            <p className="text-sm font-medium text-state-warn-light dark:text-state-warn-dark leading-relaxed">
              {t('mfa.proTip')}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
